import React, { useCallback, useEffect, useState } from "react";
import { Snowflake, Sun, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import TablePagination from "@/components/TablePagination";
import { api } from "@/lib/api";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PAGE_SIZE = 25;

export default function Cards() {
  const [items, setItems] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [merchantFilter, setMerchantFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (statusFilter) params.status = statusFilter;
      if (merchantFilter) params.merchant_id = merchantFilter;
      const r = await api.get("/admin/cards", { params });
      setItems(r.data.items);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, merchantFilter, page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [statusFilter, merchantFilter, limit]);
  useEffect(() => { api.get("/admin/merchants", { params: { limit: 500 } }).then((r) => setMerchants(r.data.items)); }, []);

  const merchantsById = Object.fromEntries(merchants.map(m => [m.id, m.company_name]));

  const action = async () => {
    try {
      toast.error("Card actions are merchant-scoped (use Proxy API). Use a merchant API key for live operations.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="cards-page">
        <PageHeader title="Cards" subtitle="All issued cards across the network." />

        <Surface className="p-3 mb-4 flex flex-wrap gap-2 items-center" testId="cards-filters">
          <Select label="Merchant" value={merchantFilter} onChange={setMerchantFilter} options={[{ v: "", l: "All merchants" }, ...merchants.map(m => ({ v: m.id, l: m.company_name }))]} testId="filter-merchant" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ v: "", l: "All" }, { v: "active", l: "Active" }, { v: "frozen", l: "Frozen" }, { v: "terminated", l: "Terminated" }]} testId="filter-status" />
          <div className="ml-auto text-xs text-zinc-500 tabular-nums">{total} cards</div>
        </Surface>

        <Surface testId="cards-table-card">
          <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">Masked PAN</th>
                <th className="px-5 py-3">Merchant</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Expiry</th>
                <th className="px-5 py-3 text-right">Balance</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-xs text-zinc-500">Loading…</td></tr>
              )}
              {!loading && items.map((c) => (
                <tr key={c.id} data-testid={`card-row-${c.id}`} className="border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors duration-150">
                  <td className="px-5 py-3 font-mono text-xs">{c.masked_pan}</td>
                  <td className="px-5 py-3">{merchantsById[c.merchant_id] || c.merchant_id.slice(0, 8)}</td>
                  <td className="px-5 py-3 capitalize">{c.type}</td>
                  <td className="px-5 py-3"><StatusPill value={c.status} /></td>
                  <td className="px-5 py-3 font-mono text-xs">{c.expiry}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(c.balance)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <IconBtn onClick={() => action(c, "freeze")} title="Freeze (merchant API)" testId={`freeze-${c.id}`} icon={Snowflake} />
                      <IconBtn onClick={() => action(c, "unfreeze")} title="Unfreeze (merchant API)" testId={`unfreeze-${c.id}`} icon={Sun} />
                      <IconBtn onClick={() => action(c, "terminate")} title="Terminate (merchant API)" testId={`terminate-${c.id}`} icon={Trash} />
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-xs text-zinc-500">No cards yet. Cards are issued by merchants via the Proxy API.</td></tr>
              )}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            loading={loading}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </Surface>
      </div>
    </Layout>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs">
      <span className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="h-8 text-xs px-2 border border-zinc-200 rounded-sm bg-white focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function IconBtn({ icon: Icon, onClick, title, testId }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testId}
      className="h-7 w-7 inline-flex items-center justify-center border border-zinc-200 rounded-sm hover:bg-zinc-100 text-zinc-700 transition-colors duration-150"
    >
      <Icon size={14} />
    </button>
  );
}
